import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Search, Download, Copy, CheckCircle, AlertCircle, Newspaper, Globe, Clock, Tag, Zap, ExternalLink } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { countWords, getUrgencyColor, getCategoryColor, formatSourceToAPA, parseMarkdownContent } from './beritaUtils'
import AgentHeader from '../../components/AgentHeader'

// Component for rendering parsed markdown content without citations
const MarkdownContent = ({ content }) => {
  const { parsedContent } = parseMarkdownContent(content) || { parsedContent: content }
  
  // Clean content to remove references and bibliographies that might appear at the end
  let cleanContent = parsedContent
    // Remove various reference section patterns
    .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\n[\s\S]*$/gi, '')
    .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\*\*\s*\n[\s\S]*$/gi, '')
    // Remove numbered reference lists at the end
    .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
    // Remove author-year citation patterns
    .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
    // Remove bracketed citations
    .replace(/\[\d+\]/g, '')
    .trim()
  
  // Remove portal name from beginning, keep only city
  cleanContent = cleanContent.replace(/^.*?,\s*(CNN Indonesia|Kompas\.com|Detik\.com|Tempo\.co|Liputan6\.com|Republika|Antara|Okezone|Tribunnews|Suara\.com|BBC|Reuters|Associated Press)\s*[-–—]\s*/i, '')
  
  return (
    <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed overflow-hidden break-words">
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({node, ...props}) => <h1 className="text-sm sm:text-2xl font-bold text-text-primary mb-1 sm:mb-4 mt-1 sm:mt-6 break-words overflow-wrap-anywhere" {...props} />,
          h2: ({node, ...props}) => <h2 className="text-sm sm:text-xl font-semibold text-text-primary mb-1 sm:mb-3 mt-1 sm:mt-5 break-words overflow-wrap-anywhere" {...props} />,
          h3: ({node, ...props}) => <h3 className="text-sm sm:text-lg font-medium text-text-primary mb-1 sm:mb-2 mt-1 sm:mt-4 break-words overflow-wrap-anywhere" {...props} />,
          h4: ({node, ...props}) => <h4 className="text-sm sm:text-base font-medium text-text-primary mb-1 sm:mb-2 mt-1 sm:mt-3 break-words overflow-wrap-anywhere" {...props} />,
          p: ({node, ...props}) => <p className="mb-1 sm:mb-4 text-text-secondary leading-relaxed text-xs sm:text-base break-words overflow-wrap-anywhere" {...props} />,
          ul: ({node, ...props}) => <ul className="list-disc ml-2 sm:ml-6 mb-1 sm:mb-4 space-y-0.5 sm:space-y-2 text-text-secondary text-xs sm:text-base" {...props} />,
          ol: ({node, ...props}) => <ol className="list-decimal ml-2 sm:ml-6 mb-1 sm:mb-4 space-y-0.5 sm:space-y-2 text-text-secondary text-xs sm:text-base" {...props} />,
          li: ({node, ...props}) => <li className="mb-0.5 leading-relaxed break-words overflow-wrap-anywhere" {...props} />,
          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-red-500 pl-1 sm:pl-4 italic text-text-tertiary mb-1 sm:mb-4 text-xs sm:text-base bg-red-500/5 py-1 sm:py-2 break-words overflow-wrap-anywhere" {...props} />,
          code: ({node, inline, ...props}) => 
            inline 
              ? <code className="bg-bg-tertiary text-red-400 px-0.5 sm:px-1 py-0.5 rounded text-xs break-words overflow-wrap-anywhere" {...props} />
              : <code className="block bg-bg-tertiary text-text-secondary p-1 sm:p-3 rounded-lg mb-1 sm:mb-4 overflow-x-auto text-xs break-words" {...props} />,
          strong: ({node, ...props}) => <strong className="font-semibold text-text-primary break-words overflow-wrap-anywhere" {...props} />,
          em: ({node, ...props}) => <em className="italic text-text-secondary break-words overflow-wrap-anywhere" {...props} />,
          a: ({node, ...props}) => <a className="text-red-400 hover:text-red-300 underline break-all overflow-wrap-anywhere" target="_blank" rel="noopener noreferrer" {...props} />,
          table: ({node, ...props}) => (
            <div className="overflow-x-auto mb-2 sm:mb-6 -mx-1 sm:-mx-2">
              <table className="w-full border-collapse border border-border-secondary rounded-lg text-xs min-w-full" {...props} />
            </div>
          ),
          th: ({node, ...props}) => <th className="border border-border-secondary bg-bg-tertiary p-1 sm:p-3 text-left font-semibold text-text-primary text-xs break-words" {...props} />,
          td: ({node, ...props}) => <td className="border border-border-secondary p-1 sm:p-3 text-text-secondary text-xs break-words overflow-wrap-anywhere" {...props} />
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  )
}

const BeritaResultPage = ({ 
  query, 
  selectedOption,
  generatedContent, 
  newsSources, 
  progress, 
  isLoading, 
  error, 
  isCompleted,
  language,
  onBack, 
  onBackToSearch 
}) => {
  const [copySuccess, setCopySuccess] = useState(false)
  const [copySourcesSuccess, setCopySourcesSuccess] = useState(false)
  const contentRef = useRef(null)

  // Generate full content with proper rich text formatting for copy
  const generateFullContent = () => {
    // Create a temporary div to render rich text content
    const tempDiv = document.createElement('div')
    tempDiv.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roagento, sans-serif;
      line-height: 1.6;
      color: #000;
      background: #fff;
      padding: 20px;
      max-width: none;
    `
    
    // Process content
    if (generatedContent) {
      const { parsedContent } = parseMarkdownContent(generatedContent) || { parsedContent: generatedContent }
      
      // Clean content to remove references and bibliographies
      let cleanedContent = parsedContent
        .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\n[\s\S]*$/gi, '')
        .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\*\*\s*\n[\s\S]*$/gi, '')
        .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
        .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
        .replace(/\[\d+\]/g, '')
        .trim()
      
      // Remove portal name from beginning, keep only city
      cleanedContent = cleanedContent.replace(/^.*?,\s*(CNN Indonesia|Kompas\.com|Detik\.com|Tempo\.co|Liputan6\.com|Republika|Antara|Okezone|Tribunnews|Suara\.com|BBC|Reuters|Associated Press)\s*[-–—]\s*/i, '')
      
      // Add main title if content doesn't start with title
      const firstLine = cleanedContent.split('\n')[0]
      if (!firstLine.startsWith('#') && selectedOption?.title && firstLine.toLowerCase() !== selectedOption.title.toLowerCase()) {
        const mainTitle = document.createElement('h1')
        mainTitle.style.cssText = 'font-size: 28px; font-weight: bold; margin: 0 0 24px 0; color: #000;'
        mainTitle.textContent = selectedOption.title
        tempDiv.appendChild(mainTitle)
      }
      
      // Create content div
      const contentDiv = document.createElement('div')
      
      // Split content by lines to process each line
      const lines = cleanedContent.split('\n')
      let htmlContent = ''
      let inTable = false
      let tableRows = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // Check if this is a table row
        if (line.includes('|') && line.split('|').length > 2 && !line.match(/^\s*\|[-\s:]+\|\s*$/)) {
          if (!inTable) {
            inTable = true
            tableRows = []
          }
          const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell)
          if (cells.length > 0 && !cells.every(cell => cell.match(/^[-\s:]*$/))) {
            tableRows.push(cells)
          }
        } else {
          // Process any pending table
          if (inTable && tableRows.length > 0) {
            let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd;">'
            tableRows.forEach((row, rowIndex) => {
              const isHeader = rowIndex === 0
              const tag = isHeader ? 'th' : 'td'
              const style = isHeader 
                ? 'border: 1px solid #ddd; padding: 8px 12px; background: #f5f5f5; font-weight: 600; text-align: left;'
                : 'border: 1px solid #ddd; padding: 8px 12px;'
              
              tableHtml += '<tr>'
              row.forEach(cell => {
                tableHtml += `<${tag} style="${style}">${cell}</${tag}>`
              })
              tableHtml += '</tr>'
            })
            tableHtml += '</table>'
            htmlContent += tableHtml
            inTable = false
            tableRows = []
          }
          
          // Process non-table content
          if (line) {
            let processedLine = line
            
            // Convert markdown to HTML
            if (line.startsWith('# ')) {
              processedLine = `<h1 style="font-size: 28px; font-weight: bold; margin: 32px 0 24px 0; color: #000;">${line.substring(2)}</h1>`
            } else if (line.startsWith('## ')) {
              processedLine = `<h2 style="font-size: 22px; font-weight: 600; margin: 24px 0 16px 0; color: #000;">${line.substring(3)}</h2>`
            } else if (line.startsWith('### ')) {
              processedLine = `<h3 style="font-size: 18px; font-weight: 500; margin: 20px 0 12px 0; color: #000;">${line.substring(4)}</h3>`
            } else if (line.startsWith('#### ')) {
              processedLine = `<h4 style="font-size: 16px; font-weight: 500; margin: 16px 0 10px 0; color: #000;">${line.substring(5)}</h4>`
            } else if (line.startsWith('##### ')) {
              processedLine = `<h5 style="font-size: 14px; font-weight: 500; margin: 16px 0 8px 0; color: #000;">${line.substring(6)}</h5>`
            } else if (line.startsWith('###### ')) {
              processedLine = `<h6 style="font-size: 13px; font-weight: 500; margin: 12px 0 8px 0; color: #000;">${line.substring(7)}</h6>`
            } else if (line.startsWith('> ')) {
              processedLine = `<blockquote style="border-left: 4px solid #ef4444; padding: 12px 16px; margin: 16px 0; font-style: italic; color: #666; background: rgba(239, 68, 68, 0.05); border-radius: 4px;">${line.substring(2)}</blockquote>`
            } else if (line.match(/^[-*+]\s+/)) {
              // Handle list items
              const prevLine = i > 0 ? lines[i-1].trim() : ''
              const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
              const isPrevList = prevLine.match(/^[-*+]\s+/)
              const isNextList = nextLine.match(/^[-*+]\s+/)
              
              let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^[-*+]\s+/, '')}</li>`
              
              if (!isPrevList) {
                listItem = `<ul style="margin: 16px 0; padding-left: 20px; list-style-type: disc;">` + listItem
              }
              if (!isNextList) {
                listItem = listItem + `</ul>`
              }
              
              processedLine = listItem
            } else if (line.match(/^\d+\.\s+/)) {
              // Handle numbered lists
              const prevLine = i > 0 ? lines[i-1].trim() : ''
              const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
              const isPrevList = prevLine.match(/^\d+\.\s+/)
              const isNextList = nextLine.match(/^\d+\.\s+/)
              
              let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^\d+\.\s+/, '')}</li>`
              
              if (!isPrevList) {
                listItem = `<ol style="margin: 16px 0; padding-left: 20px; list-style-type: decimal;">` + listItem
              }
              if (!isNextList) {
                listItem = listItem + `</ol>`
              }
              
              processedLine = listItem
            } else {
              // Regular paragraph with news-style formatting
              if (i === 0) {
                // First paragraph - lead paragraph styling
                processedLine = `<p style="margin: 16px 0; line-height: 1.8; text-align: justify; font-weight: 500; font-size: 1.1em;">${line}</p>`
              } else {
                processedLine = `<p style="margin: 16px 0; line-height: 1.8; text-align: justify; text-indent: 2rem;">${line}</p>`
              }
            }
            
            // Apply inline formatting
            processedLine = processedLine
              .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
              .replace(/`([^`]+)`/g, '<code style="background: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-family: \'Monaco\', \'Consolas\', monospace; font-size: 14px;">$1</code>')
              .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #ef4444; text-decoration: underline;">$1</a>')
            
            htmlContent += processedLine
          } else {
            htmlContent += '<br>'
          }
        }
      }
      
      // Handle any remaining table
      if (inTable && tableRows.length > 0) {
        let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd;">'
        tableRows.forEach((row, rowIndex) => {
          const isHeader = rowIndex === 0
          const tag = isHeader ? 'th' : 'td'
          const style = isHeader 
            ? 'border: 1px solid #ddd; padding: 8px 12px; background: #f5f5f5; font-weight: 600; text-align: left;'
            : 'border: 1px solid #ddd; padding: 8px 12px;'
          
          tableHtml += '<tr>'
          row.forEach(cell => {
            tableHtml += `<${tag} style="${style}">${cell}</${tag}>`
          })
          tableHtml += '</tr>'
        })
        tableHtml += '</table>'
        htmlContent += tableHtml
      }
      
      contentDiv.innerHTML = htmlContent
      tempDiv.appendChild(contentDiv)
    }
    
    // Add references section if available
    if (newsSources && newsSources.length > 0) {
      const referencesTitle = document.createElement('h2')
      referencesTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
      referencesTitle.textContent = 'Sumber Berita'
      tempDiv.appendChild(referencesTitle)
      
      const referencesList = document.createElement('ol')
      referencesList.style.cssText = 'margin: 16px 0; padding-left: 20px; list-style-type: decimal;'
      
      newsSources.forEach((source, index) => {
        const listItem = document.createElement('li')
        listItem.style.cssText = 'margin: 12px 0; line-height: 1.6;'
        listItem.innerHTML = formatSourceToAPA(source, index + 1)
        referencesList.appendChild(listItem)
      })
      
      tempDiv.appendChild(referencesList)
    }
    
    // Append to body temporarily to copy rich text
    document.body.appendChild(tempDiv)
    
    // Select and copy the content as rich text
    const range = document.createRange()
    range.selectNodeContents(tempDiv)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    
    let success = false
    try {
      success = document.execCommand('copy')
    } catch (err) {
      console.error('Copy failed:', err)
    }
    
    // Clean up
    selection.removeAllRanges()
    document.body.removeChild(tempDiv)
    
    return success
  }

  const handleCopyContent = async () => {
    try {
      const success = generateFullContent()
      
      if (success) {
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      } else {
        // Fallback to plain text if rich text copy fails
        const { parsedContent } = parseMarkdownContent(generatedContent) || { parsedContent: generatedContent }
        let cleanContent = parsedContent
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\n[\s\S]*$/gi, '')
          .replace(/\[\d+\]/g, '')
          .trim()
        
        // Remove portal name from beginning
        cleanContent = cleanContent.replace(/^.*?,\s*(CNN Indonesia|Kompas\.com|Detik\.com|Tempo\.co|Liputan6\.com|Republika|Antara|Okezone|Tribunnews|Suara\.com|BBC|Reuters|Associated Press)\s*[-–—]\s*/i, '')
        
        // Add title if not already in content
        const firstLine = cleanContent.split('\n')[0]
        if (!firstLine.startsWith('#') && selectedOption?.title && firstLine.toLowerCase() !== selectedOption.title.toLowerCase()) {
          cleanContent = `${selectedOption.title}\n\n${cleanContent}`
        }
        
        // Add references
        if (newsSources && newsSources.length > 0) {
          cleanContent += '\n\nSumber Berita:\n\n'
          newsSources.forEach((source, index) => {
            const apaFormatted = formatSourceToAPA(source, index + 1)
            const plainText = apaFormatted.replace(/<[^>]*>/g, '')
            cleanContent += `${index + 1}. ${plainText}\n\n`
          })
        }
        
        await navigator.clipboard.writeText(cleanContent)
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      }
    } catch (err) {
      console.error('Failed to copy content:', err)
    }
  }

  const handleCopySources = async () => {
    try {
      if (!newsSources || newsSources.length === 0) return
      
      // Format sources as APA references in numbered list
      const formattedSources = newsSources.map((source, index) => {
        const apaFormatted = formatSourceToAPA(source, index + 1)
        // Remove HTML tags for plain text copy
        const plainText = apaFormatted.replace(/<[^>]*>/g, '')
        return `${index + 1}. ${plainText}`
      }).join('\n\n')
      
      const sourcesText = `SUMBER BERITA:\n\n${formattedSources}`
      
      await navigator.clipboard.writeText(sourcesText)
      setCopySourcesSuccess(true)
      setTimeout(() => setCopySourcesSuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy sources:', err)
    }
  }

  const handleDownload = () => {
    try {
      // Generate plain text content for download
      const { parsedContent } = parseMarkdownContent(generatedContent) || { parsedContent: generatedContent }
      let cleanContent = parsedContent
        .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography|Sumber)\s*\n[\s\S]*$/gi, '')
        .replace(/\[\d+\]/g, '')
        .trim()
      
      // Remove portal name from beginning
      cleanContent = cleanContent.replace(/^.*?,\s*(CNN Indonesia|Kompas\.com|Detik\.com|Tempo\.co|Liputan6\.com|Republika|Antara|Okezone|Tribunnews|Suara\.com|BBC|Reuters|Associated Press)\s*[-–—]\s*/i, '')
      
      // Create full content for download
      let fullContent = ''
      
      // Add title if not already in content
      const firstLine = cleanContent.split('\n')[0]
      if (!firstLine.startsWith('#') && selectedOption?.title && firstLine.toLowerCase() !== selectedOption.title.toLowerCase()) {
        fullContent = `${selectedOption.title}\n\n${cleanContent}`
      } else {
        fullContent = cleanContent
      }
      
      // Add references
      if (newsSources && newsSources.length > 0) {
        fullContent += '\n\nSumber Berita:\n\n'
        newsSources.forEach((source, index) => {
          const apaFormatted = formatSourceToAPA(source, index + 1)
          const plainText = apaFormatted.replace(/<[^>]*>/g, '')
          fullContent += `${index + 1}. ${plainText}\n\n`
        })
      }
      
      const element = document.createElement('a')
      const file = new Blob([fullContent], { type: 'text/plain; charset=utf-8' })
      element.href = URL.createObjectURL(file)
      element.download = `berita-${selectedOption?.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'news'}.txt`
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    } catch (err) {
      console.error('Failed to download content:', err)
    }
  }

  const wordCount = countWords(generatedContent)

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      <AgentHeader
        onBack={onBack}
        icon={Newspaper}
        title={language === 'id' ? 'Berita Agent' : 'News Agent'}
        query={isCompleted 
          ? (language === 'id' ? 'Berita Selesai' : 'News Completed')
          : (language === 'id' ? 'Membuat Berita...' : 'Creating News...')
        }
        iconColor="text-red-500"
        iconBgColor="bg-red-500/10"
        actions={isCompleted ? [
          {
            icon: copySuccess ? CheckCircle : Copy,
            label: copySuccess ? (language === 'id' ? 'Tersalin!' : 'Copied!') : (language === 'id' ? 'Salin' : 'Copy'),
            onClick: handleCopyContent,
            variant: "secondary",
            className: copySuccess ? "text-green-500" : ""
          },
          {
            icon: Download,
            label: language === 'id' ? 'Unduh' : 'Download',
            onClick: handleDownload,
            variant: "secondary"
          },
          {
            icon: Search,
            label: language === 'id' ? 'Berita Baru' : 'New News',
            onClick: onBackToSearch,
            variant: "primary"
          }
        ] : []}
      />

      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
        {/* Topic and Selected Option Info */}
        <div className="bg-bg-secondary rounded-xl p-2 sm:p-6 mb-3 sm:mb-6 border border-border-primary">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <h2 className="text-sm sm:text-lg font-semibold text-text-primary mb-2">
                {language === 'id' ? 'Topik Berita' : 'News Topic'}
              </h2>
              <p className="text-text-secondary text-sm sm:text-base mb-2 sm:mb-4 break-words">"{query}"</p>
              
              {selectedOption && (
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className={cn(
                      "text-xs px-2 py-1 rounded font-medium flex items-center",
                      getCategoryColor(selectedOption.category)
                    )}>
                      <Tag className="w-3 h-3 mr-1" />
                      {selectedOption.category}
                    </span>
                    <span className={cn(
                      "text-xs px-2 py-1 rounded font-medium flex items-center",
                      getUrgencyColor(selectedOption.urgency)
                    )}>
                      <Zap className="w-3 h-3 mr-1" />
                      {selectedOption.urgency === 'high' ? (language === 'id' ? 'Tinggi' : 'High') :
                       selectedOption.urgency === 'medium' ? (language === 'id' ? 'Sedang' : 'Medium') :
                       (language === 'id' ? 'Rendah' : 'Low')}
                    </span>
                    <div className="flex items-center space-x-1 text-text-tertiary">
                      <Globe className="w-3 h-3" />
                      <span className="text-xs">
                        {language === 'id' ? 'Indonesia' : 'English'}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-text-primary break-words">{selectedOption.title}</h3>
                    <p className="text-xs sm:text-sm text-text-secondary mt-1 break-words">{selectedOption.angle}</p>
                  </div>
                </div>
              )}
            </div>
            
            {isCompleted && (
              <div className="text-center sm:text-right mt-3 sm:mt-0 flex-shrink-0">
                <div className="text-xs sm:text-sm text-text-tertiary mb-1">
                  {language === 'id' ? 'Jumlah Kata' : 'Word Count'}
                </div>
                <div className="text-lg sm:text-2xl font-bold text-text-primary">{wordCount.toLocaleString()}</div>
                <div className="text-xs text-text-tertiary">
                  {language === 'id' ? 'kata' : 'words'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        {isLoading && (
          <div className="bg-bg-secondary rounded-xl p-2 sm:p-6 mb-3 sm:mb-6 border border-border-primary">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="animate-spin rounded-full h-5 w-5 sm:h-8 sm:w-8 border-b-2 border-red-500 flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-text-primary mb-2 text-sm sm:text-base">
                  {language === 'id' ? 'Membuat Berita...' : 'Creating News...'}
                </h3>
                <div className="w-full bg-bg-tertiary rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs sm:text-sm text-text-tertiary mt-2 break-words">
                  {language === 'id' ? 'Menganalisis sumber berita dan membuat konten...' : 'Analyzing news sources and creating content...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-2 sm:p-4 mb-3 sm:mb-6">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-500 font-medium text-sm sm:text-base break-words leading-relaxed">{error}</p>
            </div>
          </div>
        )}

        {/* Generated Content */}
        {generatedContent && (
          <div className="space-y-3 sm:space-y-6">
            <div className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
              <div className="p-2 sm:p-6 border-b border-border-primary">
                <h2 className="text-sm sm:text-xl font-semibold text-text-primary">
                  {language === 'id' ? 'Artikel Berita' : 'News Article'}
                </h2>
              </div>
              <div className="p-2 sm:p-6">
                <MarkdownContent content={generatedContent} />
              </div>
            </div>

            {/* News Sources */}
            {newsSources && newsSources.length > 0 && (
              <div className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
                <div className="p-2 sm:p-6 border-b border-border-primary">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                    <div>
                      <h2 className="text-sm sm:text-xl font-semibold text-text-primary">
                        {language === 'id' ? 'Sumber Berita' : 'News Sources'}
                      </h2>
                      <p className="text-text-secondary mt-1 text-xs sm:text-sm">
                        {newsSources.length} {language === 'id' ? 'sumber referensi' : 'reference sources'}
                      </p>
                    </div>
                    <button
                      onClick={handleCopySources}
                      className={cn(buttonVariants.secondary, "flex items-center text-xs sm:text-sm px-1.5 sm:px-3 py-1 sm:py-2 whitespace-nowrap flex-shrink-0")}
                    >
                      {copySourcesSuccess ? (
                        <>
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-green-500" />
                          <span className="hidden sm:inline">{language === 'id' ? 'Tersalin!' : 'Copied!'}</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="hidden sm:inline">{language === 'id' ? 'Salin Sumber' : 'Copy Sources'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* APA Formatted References */}
                <div className="p-2 sm:p-6">
                  <div className="space-y-2 sm:space-y-3">
                    {newsSources.slice(0, 15).map((source, index) => (
                      <div key={index} className="flex items-start space-x-1.5 sm:space-x-3">
                        <div className="flex-shrink-0 w-4 h-4 sm:w-6 sm:h-6 bg-red-500/10 rounded-full flex items-center justify-center mt-1">
                          <span className="text-xs font-medium text-red-500">{index + 1}</span>
                        </div>
                        <div 
                          className="flex-1 text-xs sm:text-sm text-text-secondary leading-relaxed p-1.5 sm:p-3 bg-bg-tertiary rounded-lg border border-border-secondary reference-item break-words overflow-hidden"
                          dangerouslySetInnerHTML={{ __html: formatSourceToAPA(source, index + 1) }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Completion Message */}
            {isCompleted && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-2 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-green-500 mb-1 text-sm sm:text-base">
                      {language === 'id' ? 'Berita Berhasil Dibuat!' : 'News Successfully Created!'}
                    </h3>
                    <p className="text-xs sm:text-sm text-green-400 break-words leading-relaxed">
                      {language === 'id' 
                        ? `Artikel berita dengan ${wordCount.toLocaleString()} kata telah selesai dibuat dengan referensi dari ${newsSources?.length || 0} sumber terpercaya.`
                        : `News article with ${wordCount.toLocaleString()} words has been created with references from ${newsSources?.length || 0} trusted sources.`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

BeritaResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  selectedOption: PropTypes.object,
  generatedContent: PropTypes.string,
  newsSources: PropTypes.array,
  progress: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  isCompleted: PropTypes.bool,
  language: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired
}

export default BeritaResultPage